import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErrorText } from "../lib/api";
import { todayNepal } from "../lib/partyBook";
import { fmtTeamCredit, TEAM_CREDIT_CURRENCIES } from "../lib/teamCredit";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Textarea } from "./ui/textarea";

const emptyEntry = (teamId = "", memberId = "", currency = "USD") => ({
  team_id: teamId,
  member_id: memberId,
  date: todayNepal(),
  direction: "credit",
  currency,
  amount: "",
  reference: "",
  notes: "",
});

const formFromEntry = (entry, teamId) => ({
  team_id: entry.team_id || teamId,
  member_id: entry.member_id || "",
  date: entry.date || todayNepal(),
  direction: entry.direction || "credit",
  currency: entry.currency || "USD",
  amount: String(entry.amount ?? entry.amount_usd ?? ""),
  reference: entry.reference || "",
  notes: entry.notes || "",
});

export default function TeamCreditEntryDialog({
  open,
  onOpenChange,
  team,
  members,
  initialMemberId = "",
  initialCurrency = "USD",
  entry = null,
  onSaved,
}) {
  const defaultMemberId = initialMemberId || members?.[0]?.id || "";
  const [form, setForm] = useState(emptyEntry(team?.id, defaultMemberId, initialCurrency));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(entry
      ? formFromEntry(entry, team?.id || "")
      : emptyEntry(team?.id || "", defaultMemberId, initialCurrency));
  }, [open, entry, team?.id, defaultMemberId, initialCurrency]);

  const saveEntry = async () => {
    const amount = Number(form.amount);
    if (!form.member_id) return toast.error("Select a member");
    if (!amount || amount <= 0) return toast.error(`Enter a valid ${form.currency} amount`);

    setSaving(true);
    try {
      const payload = {
        team_id: team.id,
        member_id: form.member_id,
        date: form.date,
        direction: form.direction,
        currency: form.currency,
        amount,
        reference: form.reference.trim() || null,
        notes: form.notes.trim() || null,
      };
      if (entry?.id) {
        await api.put(`/team-credit/entries/${entry.id}`, payload);
      } else {
        await api.post("/team-credit/entries", payload);
      }
      toast.success(entry?.id ? "Entry updated" : `${form.direction === "credit" ? "Credit" : "Debit"} added`);
      onOpenChange(false);
      await onSaved?.();
    } catch (error) {
      toast.error(apiErrorText(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">{entry?.id ? "Edit credit entry" : "Add credit entry"}</DialogTitle>
          <DialogDescription>
            Record an NPR, USD, or USDT credit or debit for one member of {team?.name || "this team"}.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Member *</Label>
            <Select value={form.member_id || "__none"} onValueChange={(value) => setForm({ ...form, member_id: value === "__none" ? "" : value })}>
              <SelectTrigger data-testid="team-credit-member-select"><SelectValue placeholder="Select member" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none" disabled>Select member</SelectItem>
                {(members || []).map((member) => <SelectItem key={member.id} value={member.id}>{member.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Type *</Label>
            <Select value={form.direction} onValueChange={(value) => setForm({ ...form, direction: value })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="credit">Credit</SelectItem>
                <SelectItem value="debit">Debit</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Date (Nepal) *</Label>
            <Input type="date" value={form.date} onChange={(event) => setForm({ ...form, date: event.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>Currency *</Label>
            <Select value={form.currency} onValueChange={(value) => setForm({ ...form, currency: value })}>
              <SelectTrigger data-testid="team-credit-currency-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TEAM_CREDIT_CURRENCIES.map((currency) => <SelectItem key={currency} value={currency}>{currency}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Amount {form.currency} *</Label>
            <Input type="number" min="0" step={form.currency === "USDT" ? "0.0001" : "0.01"} inputMode="decimal" value={form.amount} onChange={(event) => setForm({ ...form, amount: event.target.value })} placeholder="0.00" />
            <p className="text-[11px] text-zinc-500">Ledger amount: {fmtTeamCredit(form.amount, form.currency)}</p>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Reference</Label>
            <Input value={form.reference} onChange={(event) => setForm({ ...form, reference: event.target.value })} placeholder="Invoice, transfer, or reference" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} placeholder="Optional note" rows={3} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={saveEntry} disabled={saving || !members?.length} className="bg-zinc-900 text-white hover:bg-zinc-800">
            {saving ? "Saving..." : entry?.id ? "Update entry" : "Save entry"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
